'use client';

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { BusinessReportSourcesTab } from './BusinessReportSourcesTab';
import { BusinessRevenueSourcesTab } from './BusinessRevenueSourcesTab';
import { BusinessRevenueCategoryMappingsTab } from './BusinessRevenueCategoryMappingsTab';
import { BusinessRevenueSourceDefinitionsTab } from './BusinessRevenueSourceDefinitionsTab';
import { businessReportRoute, normalizeBusinessReportPath } from '../lib/business-report-routes';

type DataSourcesWorkspaceTab = 'data-sources' | 'revenue-sources' | 'revenue-category-rules' | 'source-onboarding';

const DATA_SOURCES_ROUTE = '/dashboard/business-reports/data-sources';
const LEGACY_REVENUE_SOURCES_ROUTE = '/dashboard/business-reports/revenue-sources';
const LEGACY_REVENUE_CATEGORY_RULES_ROUTE = '/dashboard/business-reports/revenue-category-rules';
const LEGACY_SOURCE_ONBOARDING_ROUTE = '/dashboard/business-reports/source-onboarding';

const WORKSPACE_TABS: Array<{ key: DataSourcesWorkspaceTab; label: string; description: string }> = [
  { key: 'data-sources', label: 'Data Sources', description: 'Technical connectors and schedules' },
  { key: 'revenue-sources', label: 'Revenue Sources', description: 'Revenue streams and contribution' },
  { key: 'revenue-category-rules', label: 'Revenue Category Rules', description: 'Source categories and amount mapping' },
  { key: 'source-onboarding', label: 'Source Onboarding', description: 'Validation, approval and activation' },
];

function isWorkspaceTab(value: string | null): value is DataSourcesWorkspaceTab {
  return value === 'data-sources' || value === 'revenue-sources' || value === 'revenue-category-rules' || value === 'source-onboarding';
}

function tabFromLocation(): DataSourcesWorkspaceTab {
  if (typeof window === 'undefined') return 'data-sources';
  const normalizedPath = normalizeBusinessReportPath(window.location.pathname);
  if (normalizedPath === LEGACY_REVENUE_SOURCES_ROUTE) return 'revenue-sources';
  if (normalizedPath === LEGACY_REVENUE_CATEGORY_RULES_ROUTE) return 'revenue-category-rules';
  if (normalizedPath === LEGACY_SOURCE_ONBOARDING_ROUTE) return 'source-onboarding';
  const requestedTab = new URLSearchParams(window.location.search).get('tab');
  return isWorkspaceTab(requestedTab) ? requestedTab : 'data-sources';
}

function canonicalUrl(tab: DataSourcesWorkspaceTab) {
  return businessReportRoute('data-sources', `tab=${tab}`);
}

export function BusinessReportDataSourcesWorkspace({
  active,
  token,
  onTopBarActionsChange,
}: {
  active: boolean;
  token?: string;
  onTopBarActionsChange?: (actions: ReactNode) => void;
}) {
  const [selectedTab, setSelectedTab] = useState<DataSourcesWorkspaceTab>('data-sources');
  const [locationReady, setLocationReady] = useState(false);

  const syncFromLocation = useCallback(() => {
    const nextTab = tabFromLocation();
    setSelectedTab(nextTab);
    if (normalizeBusinessReportPath(window.location.pathname) !== DATA_SOURCES_ROUTE || !isWorkspaceTab(new URLSearchParams(window.location.search).get('tab'))) {
      window.history.replaceState({}, '', canonicalUrl(nextTab));
    }
  }, []);

  useEffect(() => {
    if (!active) {
      setLocationReady(false);
      return undefined;
    }
    syncFromLocation();
    setLocationReady(true);
    const handlePopState = () => syncFromLocation();
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [active, syncFromLocation]);

  if (!active) return null;

  const selectTab = (tab: DataSourcesWorkspaceTab) => {
    if (tab === selectedTab && normalizeBusinessReportPath(window.location.pathname) === DATA_SOURCES_ROUTE) return;
    setSelectedTab(tab);
    window.history.pushState({}, '', canonicalUrl(tab));
  };

  return (
    <section className="business-admin-workspace business-data-sources-workspace" aria-label="Business Reports data source workspace">
      <nav className="business-admin-workspace-tabs" role="tablist" aria-label="Data source administration sections">
        {WORKSPACE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            id={`business-data-source-tab-${tab.key}`}
            className={selectedTab === tab.key ? 'active' : ''}
            role="tab"
            aria-selected={selectedTab === tab.key}
            aria-controls={`business-data-source-panel-${tab.key}`}
            tabIndex={selectedTab === tab.key ? 0 : -1}
            onClick={() => selectTab(tab.key)}
          >
            <strong>{tab.label}</strong>
            <span>{tab.description}</span>
          </button>
        ))}
      </nav>

      <div className="business-admin-workspace-content" id={`business-data-source-panel-${selectedTab}`} role="tabpanel" aria-labelledby={`business-data-source-tab-${selectedTab}`}>
        <BusinessReportSourcesTab active={locationReady && selectedTab === 'data-sources'} token={token} onTopBarActionsChange={onTopBarActionsChange} />
        <BusinessRevenueSourcesTab active={locationReady && selectedTab === 'revenue-sources'} token={token} onTopBarActionsChange={onTopBarActionsChange} />
        <BusinessRevenueCategoryMappingsTab active={locationReady && selectedTab === 'revenue-category-rules'} token={token} onTopBarActionsChange={onTopBarActionsChange} />
        <BusinessRevenueSourceDefinitionsTab active={locationReady && selectedTab === 'source-onboarding'} token={token} onTopBarActionsChange={onTopBarActionsChange} />
      </div>
    </section>
  );
}
